/**
 * `PropensionSelect` — selettore controllato della **Propensione**
 * (`0..10`), il voto informativo "volontà di approfondire" (interesse a
 * crescere sulla skill).
 *
 * Sostituisce lo `StarRating` come input del voto informativo. Segue lo stesso
 * pattern di `ExperienceRangeSelect`: è un dato **puramente informativo e
 * opzionale**, quindi lo stato "nessuna preferenza" (`null`) è finale e sempre
 * riselezionabile tramite l'opzione placeholder. Il componente non salva né
 * valida: si limita a notificare la scelta al genitore.
 *
 * Nota sui nomi: il campo dati resta `selfInterest` in DTO/model; "Propensione"
 * è solo copy rivolto all'utente. La scala `0..10` è resa esplicita nelle
 * opzioni mostrando sia il valore numerico sia l'etichetta.
 */
import { useId } from 'react';

/**
 * Etichette testuali della Propensione per ciascun valore `0..10`. Sono copy
 * rivolto all'utente (italiano) e non incidono sul dato memorizzato.
 */
export const PROPENSION_LABELS: Record<number, string> = {
  0: 'Nessuna propensione',
  1: 'Quasi nulla',
  2: 'Molto bassa',
  3: 'Bassa',
  4: 'Abbastanza bassa',
  5: 'Media',
  6: 'Discreta',
  7: 'Buona',
  8: 'Alta',
  9: 'Molto alta',
  10: 'Massima propensione',
};

/** Valori ammessi della Propensione, in ordine crescente (`0..10`). */
export const PROPENSION_VALUES: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Opzioni selezionabili nel `PropensionSelect`: una scala semplificata a tre
 * gradi (Nessuna / Media / Alta) mappata sui valori della scala `0..10`. Il
 * campo dati resta `selfInterest` numerico: la scala completa `0..10` è ancora
 * valida (e resa da {@link propensionLabel} nelle viste in sola lettura), ma
 * l'input di autovalutazione propone solo questi tre gradi per semplicità.
 */
export const PROPENSION_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 0, label: 'Nessuna' },
  { value: 5, label: 'Media' },
  { value: 10, label: 'Alta' },
];

/** Placeholder testuale mostrato quando la Propensione non è espressa (`null`). */
export const PROPENSION_ABSENT_LABEL = '—';

/**
 * Verifica che un valore sia una Propensione valida: intero nell'intervallo
 * `0..10`.
 */
export function isPropension(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 10;
}

/**
 * Restituisce l'etichetta della Propensione per un valore `0..10`, oppure un
 * placeholder ("—") quando il valore è `null` (nessuna preferenza espressa).
 */
export function propensionLabel(value: number | null): string {
  if (value === null) return PROPENSION_ABSENT_LABEL;
  return PROPENSION_LABELS[value] ?? PROPENSION_ABSENT_LABEL;
}

export interface PropensionSelectProps {
  /** Propensione corrente (`0..10`), oppure `null` se non espressa. */
  value: number | null;
  /**
   * Notifica la Propensione scelta dall'utente: un valore valido (`0..10`)
   * oppure `null` quando l'utente riseleziona il placeholder (dato opzionale).
   */
  onChange: (value: number | null) => void;
  /** Etichetta accessibile del controllo (es. "Propensione per React"). */
  label: string;
  /** Disabilita l'interazione (sola lettura / non modificabile). */
  disabled?: boolean;
  /** Testo dell'opzione placeholder mostrata quando `value` è `null`. */
  placeholder?: string;
  /** `id` opzionale per collegare una `<label>` esterna. */
  id?: string;
  /** Classe extra sul `<select>`. */
  className?: string;
}

/**
 * Select opzionale della Propensione. Il placeholder è sempre selezionabile e
 * rappresenta lo stato "nessuna preferenza" (`null`). Le opzioni sono i tre
 * gradi di {@link PROPENSION_OPTIONS} (Nessuna / Media / Alta), mostrati con la
 * sola etichetta testuale; il valore inviato resta sulla scala `0..10`.
 */
export function PropensionSelect({
  value,
  onChange,
  label,
  disabled = false,
  placeholder = 'Propensione…',
  id,
  className,
}: PropensionSelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = event.target.value;
    if (raw === '') {
      // Placeholder (valore vuoto): il campo è opzionale, quindi azzerabile.
      onChange(null);
      return;
    }
    const parsed = Number.parseInt(raw, 10);
    if (isPropension(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <select
      id={selectId}
      className={className ? `propension-select ${className}` : 'propension-select'}
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      data-empty={value === null || undefined}
      onChange={handleChange}
    >
      {/* Opzione placeholder: stato "nessuna preferenza" (`null`), sempre
          selezionabile perché il campo è opzionale. */}
      <option value="">{placeholder}</option>
      {/* Scala semplificata a tre gradi (Nessuna / Media / Alta), senza numero
          nella label. Il valore inviato resta sulla scala `0..10`. */}
      {PROPENSION_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export default PropensionSelect;
